/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Focusable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type LoadingStatus } from '../../../common/ui/loading-status-indicator';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { LoadingStepContainer, LoadingStepTitle } from '../loading-step';

import intlMessages from './messages';

const styles = cssMap({
	titleContainer: {
		display: 'flex',
		flexDirection: 'row',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.medium'),
	},
	expandablePressable: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			cursor: 'pointer',
		},
		transition: 'background-color 0.2s ease',
		minHeight: '32px',
	},
	chevronContainer: {
		marginRight: token('space.100'),
	},
	expandedContent: {
		width: '100%',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		wordBreak: 'break-word',
	},
});

export type ExpandableLoadingStepProps = {
	testId?: string;
	/** main text content of the loading step  */
	title?: string;
	/** determines the status icon displayed */
	status: LoadingStatus;
	/** called when the step is clicked for expansion */
	onClick: () => void;
	/** expanded state */
	isExpanded?: boolean;
	isLastItem?: boolean;
	/** content displayed when item is expanded */
	children: ReactNode;
};

export const ExpandableLoadingStep = ({
	title,
	onClick,
	testId = 'rovo-expandable-step',
	isExpanded = false,
	isLastItem = false,
	status,
	children,
}: ExpandableLoadingStepProps) => {
	const { formatMessage } = useIntl();

	return (
		<LoadingStepContainer testId={testId} status={status} isLastItem={isLastItem}>
			<Focusable
				as="div"
				xcss={cx(styles.titleContainer, styles.expandablePressable)}
				testId={testId}
				onClick={onClick}
				onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						onClick();
					}
				}}
				role="button"
				tabIndex={0}
				data-expanded={isExpanded}
			>
				<LoadingStepTitle title={title} />
				<Box xcss={styles.chevronContainer}>
					{isExpanded ? (
						<ChevronDownIcon
							label={formatMessage(intlMessages.collapseIconLabel)}
							size="small"
							color={token('color.icon.subtle')}
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(intlMessages.expandIconLabel)}
							size="small"
							color={token('color.icon.subtle')}
						/>
					)}
				</Box>
			</Focusable>
			{isExpanded && <Box xcss={styles.expandedContent}>{children}</Box>}
		</LoadingStepContainer>
	);
};
